import { api } from "@/shared/api";
import type { components } from "@/lib/api/generated";
import type { Override } from "@/shared/lib/typeOverride";

// ── Types ──────────────────────────────────────────────────────────────

// local-only: A (frontend union type alias); see proposals/2026-05-17-fe-generated-coverage-user.md
/** 业务模块:AI客服 / AI陪练。 */
export type WeComModule = "customer_service" | "coaching";

/** 身份模式:MEMBER_CHANNEL(纯客服终端) | TENANT_CHAIN(军师工作台三脸)。 */
export type WeComIdentityMode = "MEMBER_CHANNEL" | "TENANT_CHAIN";

export type WeComApp = Override<
  components["schemas"]["WeComAppResponse"],
  {
    id: number;
    /** 1:1 关联 tenant_channels.id(注册表)。 */
    channelId: number;
    tenantId: number;
    corpId: string;
    agentId: number | null;
    hasAppSecret: boolean;
    hasCallbackToken: boolean;
    hasEncodingAesKey: boolean;
    /** 注册表 display_name。 */
    label: string;
    /** 注册表 enabled。 */
    enabled: boolean;
    callbackDomain: string | null;
    callbackUrl: string | null;
    homepageUrl: string | null;
    module: WeComModule;
    /** 身份模式;TENANT_CHAIN = 军师工作台已启用(homepageUrl 落 advisor-h5)。 */
    identityMode: WeComIdentityMode;
    // Drives {company_name} in the customer-service prompt.
    companyName?: string | null;
    createdAt: string;
    updatedAt: string;
    // mark: not yet in api-spec.yaml (V24 proposal)
  }
>;

// local-only: B (backend schema gap) — POST/PUT /tenant/wecom/apps body
export interface WeComAppRequest {
  corpId: string;
  agentId?: number | null;
  appSecret?: string;
  callbackToken?: string;
  encodingAesKey?: string;
  label?: string;
  enabled?: boolean;
  module?: WeComModule;
  callbackDomain?: string;
  companyName?: string | null;
  identityMode?: WeComIdentityMode;
}

export type KfAccount = Override<
  components["schemas"]["KfAccountResponse"],
  {
    id: number;
    appConfigId: number;
    openKfid: string;
    tenantCode: string;
    kfName: string;
    createdAt: string;
    // mark: not yet in api-spec.yaml (V24 proposal)
  }
>;

// local-only: B (backend schema gap) — POST/PUT KfAccount body
export interface KfAccountRequest {
  openKfid: string;
  kfName?: string;
}

// local-only: B (backend schema gap) — /kf-accounts/remote 返
export interface RemoteKfAccount {
  openKfid: string;
  name: string;
  avatar: string | null;
  bound: boolean;
}

// local-only: B (backend schema gap) — POST /kf-accounts/bind 入参项
export interface RemoteKfAccountItem {
  openKfid: string;
  name: string;
  avatar: string | null;
}

// ── Service ────────────────────────────────────────────────────────────

export const wecomAppService = {
  listApps(tenantId: number | string, module?: string) {
    const params = module ? `?module=${module}` : "";
    return api.get<WeComApp[]>(`/tenant/wecom/apps${params}`);
  },

  createApp(tenantId: number | string, req: WeComAppRequest) {
    return api.post<WeComApp>(`/tenant/wecom/apps`, req);
  },

  updateApp(tenantId: number | string, appId: number, req: WeComAppRequest) {
    return api.put<WeComApp>(`/tenant/wecom/apps/${appId}`, req);
  },

  deleteApp(tenantId: number | string, appId: number) {
    return api.delete<void>(`/tenant/wecom/apps/${appId}`);
  },

  /** 测试企微连通(后端主动握手 gettoken;失败抛 UPSTREAM_API_ERROR)。 */
  testConnection(tenantId: number | string, appId: number) {
    return api.post<void>(`/tenant/wecom/apps/${appId}/test`, null);
  },

  // ── KF Account (AI客服 only) ──────────────────────────────────────

  listKfAccounts(tenantId: number | string, appId: number) {
    return api.get<KfAccount[]>(`/tenant/wecom/apps/${appId}/kf-accounts`);
  },

  createKfAccount(tenantId: number | string, appId: number, req: KfAccountRequest) {
    return api.post<KfAccount>(`/tenant/wecom/apps/${appId}/kf-accounts`, req);
  },

  updateKfAccount(tenantId: number | string, appId: number, kfId: number, req: KfAccountRequest) {
    return api.put<KfAccount>(`/tenant/wecom/apps/${appId}/kf-accounts/${kfId}`, req);
  },

  deleteKfAccount(tenantId: number | string, appId: number, kfId: number) {
    return api.delete<void>(`/tenant/wecom/apps/${appId}/kf-accounts/${kfId}`);
  },

  listRemoteKfAccounts(tenantId: number | string, appId: number) {
    return api.get<RemoteKfAccount[]>(`/tenant/wecom/apps/${appId}/kf-accounts/remote`);
  },

  bindKfAccounts(tenantId: number | string, appId: number, items: RemoteKfAccountItem[]) {
    return api.post<number>(`/tenant/wecom/apps/${appId}/kf-accounts/bind`, items);
  },

  unbindKfAccounts(tenantId: number | string, appId: number, openKfids: string[]) {
    return api.post<number>(`/tenant/wecom/apps/${appId}/kf-accounts/unbind`, openKfids);
  },
};

// ── 企微域名验证文件(OEM D1)──────────────────────────────────────────
// 企微配置可信域名时要求 WW_verify_*.txt 放在域名根目录;租户在控制台上传入库,
// 后端按 Host 对 `/WW_verify_*.txt` 回文件内容(入口把该路径转给后端)。

// local-only: B (backend schema gap) — 契约 = api d01cf328 WeComVerifyFileController
export interface WeComVerifyFile {
  id: number;
  /** 该文件服务的回调域名(租户 callback_domain 之一)。 */
  host: string;
  fileName: string;
  content?: string | null;
  /** 企微校验时访问的完整地址(https://<host>/<fileName>)。 */
  url?: string | null;
  createdAt?: string | null;
}

// local-only: B (backend schema gap) — GET /tenant/wecom/verify-files 返
export interface WeComVerifyFileList {
  files: WeComVerifyFile[];
  /** 租户已配的回调域名;空 = 先去企微应用里配回调域名才能上传。 */
  callbackHosts: string[];
}

/** 企微验证文件名:WW_verify_<字母数字>.txt(企微后台下载的原文件名)。 */
export const WECOM_VERIFY_FILE_NAME_RE = /^WW_verify_[A-Za-z0-9]+\.txt$/;
/** 验证文件内容是十几字节的随机串;前端先挡 >128B 的误传。 */
export const WECOM_VERIFY_FILE_MAX_BYTES = 128;

export type WeComVerifyFileProblem = "invalidName" | "tooLarge" | "emptyFile";

/** 上传前本地校验;通过返回 null。 */
export function checkWeComVerifyFile(
  file: Pick<File, "name" | "size">
): WeComVerifyFileProblem | null {
  if (!WECOM_VERIFY_FILE_NAME_RE.test(file.name)) return "invalidName";
  if (file.size > WECOM_VERIFY_FILE_MAX_BYTES) return "tooLarge";
  if (file.size <= 0) return "emptyFile";
  return null;
}

export const wecomVerifyFileService = {
  /** 读:TENANT_MANAGER 及以上。 */
  async list(): Promise<WeComVerifyFileList> {
    const r = await api.get<Partial<WeComVerifyFileList> | null>(`/tenant/wecom/verify-files`);
    return {
      files: Array.isArray(r?.files) ? r.files : [],
      callbackHosts: Array.isArray(r?.callbackHosts) ? r.callbackHosts : [],
    };
  },

  /**
   * 写:TENANT_ADMIN。multipart `file`(必填)+ `host`(可选;租户只有一个回调 host 时可不传)。
   * 409 = 同名文件已存在。
   */
  upload(file: File, host?: string | null) {
    const form = new FormData();
    form.append("file", file);
    if (host) form.append("host", host);
    return api.post<WeComVerifyFile>(`/tenant/wecom/verify-files`, form);
  },

  remove(id: number) {
    return api.delete<void>(`/tenant/wecom/verify-files/${id}`);
  },
};
